import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import {
  getLoginRedirectOptionsForIdentifier,
  getSsoLoginOptions,
  resolveSsoRoute,
} from './Auth/ssoRouting';

const theme = createTheme({
  palette: {
    primary: {
      main: '#54B948',
    },
  },
  typography: {
    fontFamily: '"Segoe UI", Arial, sans-serif',
    fontSize: 12,
  },
});

function getAuthErrorAlert() {
  try {
    const searchParams = new URLSearchParams(window.location.search || '');
    const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    const error = searchParams.get('error') || hashParams.get('error');
    const errorDescription = searchParams.get('error_description') || hashParams.get('error_description');
    if (!error && !errorDescription) return null;
    const normalizedDescription = String(errorDescription || '').toLowerCase();
    if (
      error === 'invalid_request' &&
      (normalizedDescription.indexOf('connection is not enabled') >= 0 || normalizedDescription.indexOf('connection was not found') >= 0)
    ) {
      return 'Your company has not enabled single sign-on (SSO) for this application. Please enter your SKYPAD username instead.';
    }
    return errorDescription || error || 'Authentication failed. Please try again.';
  } catch (e) {
    return null;
  }
}

const cardSx = {
  px: { xs: 3, sm: 4 },
  py: { xs: 4, sm: 6 },
  borderRadius: 2,
  border: '1px solid #e0e0e0',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  backgroundColor: '#fff',
};

function LoginEntryPage({ onSubmit, isAuthenticating = false }) {
  const routeHint = useMemo(() => resolveSsoRoute(), []);
  const routeLogin = useMemo(() => getSsoLoginOptions(), []);
  const [identifier, setIdentifier] = useState(
    routeHint && routeHint.loginHint ? routeHint.loginHint : ''
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const authErrorMessage = useMemo(() => getAuthErrorAlert(), []);
  const loginRoute = useMemo(
    () => getLoginRedirectOptionsForIdentifier(identifier.trim()),
    [identifier]
  );

  const isBusy = Boolean(isAuthenticating || isSubmitting);
  const hasRouteSso = Boolean(routeLogin.tenantKey);
  const hasUnsupportedSsoEmail = loginRoute.isEmail && !loginRoute.tenantKey;
  const isInvalidEmail =
    identifier.indexOf('@') >= 0 &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier.trim());

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedIdentifier = identifier.trim();
    if (!trimmedIdentifier && !hasRouteSso) {
      setErrorMessage('Enter your username or email to continue.');
      return;
    }
    setErrorMessage('');
    setIsSubmitting(true);
    try {
      await onSubmit(trimmedIdentifier);
      setIsSubmitting(false);
    } catch (error) {
      setErrorMessage(
        error && error.message
          ? error.message
          : "We couldn't start login. Please try again."
      );
      setIsSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ py: 4, width: '100%', maxWidth: 360, mx: 'auto' }}>
        <Card component="form" onSubmit={handleSubmit} sx={cardSx}>
          <Stack spacing={3.5}>
            <Box sx={{ textAlign: 'center', pb: 0.5 }}>
              <Box
                component="img"
                src="https://red.skypad.biz/qlikview/images/SKYPAD%20Logo.png"
                alt="SKYPAD"
                sx={{ width: '100%', height: 'auto' }}
              />
            </Box>

            <Box>
              <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.7 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  SSO clients:
                </Box>{' '}
                Enter your company email address
              </Typography>
              <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.7 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Non-SSO clients:
                </Box>{' '}
                Enter your SKYPAD username
              </Typography>
            </Box>

            {authErrorMessage && (
              <>
                <Alert severity="warning">{authErrorMessage}</Alert>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => window.location.replace('/')}
                  sx={{
                    mt: 2,
                    borderRadius: 1.5,
                    borderColor: '#54b948',
                    color: '#54b948',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { borderColor: '#49a93f', color: '#49a93f', backgroundColor: 'rgba(84,185,72,0.05)' },
                  }}
                >
                  Back
                </Button>
              </>
            )}
            {!authErrorMessage && errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            {!authErrorMessage && isInvalidEmail && (
              <Alert severity="error">Enter a valid email address.</Alert>
            )}
            {!authErrorMessage && !isInvalidEmail && hasUnsupportedSsoEmail && (
              <Alert severity="error">
                Your company has not enabled single sign-on (SSO). Please enter
                your SKYPAD username.
              </Alert>
            )}

            {!authErrorMessage && (
              <Stack spacing={2.5} sx={{ mt: 1.5 }}>
                <TextField
                  autoFocus
                  fullWidth
                  label="Company email or username*"
                  value={identifier}
                  onChange={(event) => {
                    setIdentifier(event.target.value);
                    if (errorMessage) setErrorMessage('');
                  }}
                  disabled={isBusy}
                />

                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  fullWidth
                  disabled={isBusy || hasUnsupportedSsoEmail || isInvalidEmail}
                  sx={{
                    py: 1.4,
                    borderRadius: 1.5,
                    backgroundColor: '#54b948',
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#49a93f' },
                    '&.Mui-disabled': { backgroundColor: '#a5d6a0', color: '#fff' },
                  }}
                >
                  {isBusy ? 'Continuing...' : 'Continue'}
                </Button>
              </Stack>
            )}
          </Stack>
        </Card>
      </Box>
    </Box>
    </ThemeProvider>
  );
}

export default LoginEntryPage;
